import React, { useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import { Alert } from "../../components/Shared/Alert";
import { usePerson } from "../../data/people/usePerson";
import { PageHeader } from "../../components/Shared/PageHeader";
import LoadingWheel from "../../components/Shared/LoadingWheel";
import { ButtonInput } from "../../components/Shared/Inputs/ButtonInput";
import { LeadInformation } from "../../components/Person/LeadInformation";
import { PersonEnquiries } from "../../components/Person/PersonEnquiries";
import { usePeopleMutations } from "../../data/people/usePeopleMutations";
import useNotification from "../../components/notification/useNotifications";
import { ContactInformation } from "../../components/Person/ContactInformation";
import { EmailCorrespondence } from "../../components/Person/EmailCorrespondence";
import { MarketingGroupMembership } from "../../components/Person/MarketingGroupMembership";
import { CreateUpdatePersonFlyout } from "../../components/Person/CreateUpdatePersonFlyout";

export default function Person() {
  const { addNotification } = useNotification();
  const { personId } = useParams<{ personId: string }>();
  const { person: personData } = usePerson(personId);
  const { update } = usePeopleMutations(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const invalidId = personId === undefined;

  useEffect(() => {
    document.title = `EstateTrack Portal | Contact Details`;
  }, []);

  const person = useMemo(() =>
    personData.data?.data, [personData.data]
  )

  const toggleSubscribed = () => {
    update.mutate({
      ...person!,
      unsubscribed: !person?.unsubscribed,
    }, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: "Saved",
        });
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to save"
        });
      }
    });
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {personData.isLoading ? (
        <div className="flex items-center justify-center py-10"><LoadingWheel width="w-10" height="h-10" /></div>
      ) : personData.isError || invalidId ? (
        <Alert type="error" title="Error" message="An error occurred, please try again. If the problem persists, contact support." />
      ) : (
        <>
          <CreateUpdatePersonFlyout
            open={updateOpen}
            setOpen={setUpdateOpen}
            person={person}
          />
          <div className="flex flex-row items-center justify-between space-y-4 mb-4">
            <PageHeader
              title={person?.firstName && person.lastname ? `${person?.firstName} ${person?.lastname}` : '(no name)'}
              description="View all details about the client here"
            />
            <div className="flex flex-row space-x-4">
              {/* @ts-ignore */}
              <ButtonInput
                label={person?.unsubscribed ? "Subscribe" : "Unsubscribe"}
                onClick={toggleSubscribed}
                isSubmit={false}
              />
              {/* @ts-ignore */}
              <ButtonInput
                label="Edit Contact"
                onClick={() => setUpdateOpen(true)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <ContactInformation {...person} />
            </div>
            <LeadInformation {...person} />
            <div className="sm:col-span-3">
              <MarketingGroupMembership
                personId={person?.id ?? ""}
                marketingGroups={person?.marketingGroups || []}
              />
            </div>
            <div className="sm:col-span-3">
              <div className="grid grid-cols-1 gap-4">
                <PersonEnquiries personId={person?.id ?? ""} agencyId={person?.agencyId ?? ""} />
                <EmailCorrespondence personId={person?.id ?? ""} />
              </div>
            </div>
          </div>
        </>
      )
      }
    </div>
  );
}